import Link from 'next/link';

import ivclass from '@invideoio/web-shared/Modules/IVClass';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import BlogTags from '@components/tag/blog-tags';

import { BlogCardProps } from 'src/types';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import { BrowserType, DEFAULT_LOGO_URL } from '@constants/common';
import { getImageSize, SizeMap } from '@utils/getImageSize';

const CARD_IMG_MAP: SizeMap = {
  [PlatformType.Desktop]: { height: 215 },
  [PlatformType.Tablet]: { height: 215 },
  [PlatformType.Mobile]: { width: 320 },
  default: { height: 215 },
};

interface BlogPostCardPropType extends BlogCardProps {
  backgroundColorClass: string;
  browser?: BrowserType;
  lazyLoadImg?: boolean;
  devicePlatform: PlatformType;
}

const BlogPostCard = (props: BlogPostCardPropType) => {
  const {
    id,
    title,
    featureImage,
    slug,
    category,
    profession,
    platform,
    browser = BrowserType.Chrome,
    backgroundColorClass,
    lazyLoadImg = true,
    devicePlatform,
  } = props;

  const tagTextClass =
    'sm:iv-text-body-xl sm:iv-m-body-xl xl:iv-text-body-3xl xl:iv-m-body-3xl iv-text-body-2xl iv-m-body-2xl';

  return (
    <Link href="/blog/[post]" as={`/blog/${slug}/`}>
      <a
        className={ivclass(
          'iv-flex iv-flex-col',
          'iv-rounded-30 sm:iv-rounded-25',
          'iv-overflow-hidden',
          'iv-h-[560px] iv-w-[380px]',
          'sm:iv-h-[460px] sm:iv-w-[320px] ',
          'hover:iv-cursor-pointer',
          backgroundColorClass,
          'article-card',
        )}
        style={{
          backfaceVisibility: 'hidden',
          transform: 'translate3d(0,0,0)',
        }}
      >
        <div
          className={ivclass(
            'iv-w-[380px] iv-min-h-[215px]',
            'sm:iv-min-h-[180px] sm:iv-w-[320px]',
            'iv-overflow-hidden',
          )}
        >
          <ImageComponent
            elementype={featureImage?.url ? 'imagekit' : 'native'}
            {...(lazyLoadImg && { loading: 'lazy' })}
            src={featureImage?.url || DEFAULT_LOGO_URL}
            sizeTransformation={getImageSize(devicePlatform, CARD_IMG_MAP)}
            height={214}
            width={380}
            alt={slug}
            className={ivclass(
              'iv-rounded-t-[30px] iv-h-full sm:iv-h-[180px] sm:iv-w-[320px]',
              'iv-overflow-hidden',
              'hover:iv-scale-110 iv-transition-all iv-duration-300',
              'iv-object-cover',
              'article-image',
            )}
          />
        </div>
        <div
          className={ivclass(
            'iv-flex iv-flex-col iv-justify-between',
            'xl:iv-p-40 iv-p-35 sm:iv-p-25',
            'iv-h-full',
          )}
        >
          <span
            className={ivclass(
              'iv-font-extrabold',
              'xl:iv-text-heading-xs xl:iv-m-heading-xs',
              'iv-text-heading-2xs iv-m-heading-2xs',
              'sm:iv-text-heading-3xs sm:iv-m-heading-3xs',
              'iv-text-black-85',
              'iv-article-title',
              'iv-line-clamp-4',
            )}
          >
            {title}
          </span>

          <div className={ivclass('iv-flex iv-gap-5')}>
            <BlogTags
              category={category}
              platform={platform}
              profession={profession}
              textClass={tagTextClass}
            />
          </div>
        </div>
      </a>
    </Link>
  );
};

export default BlogPostCard;
